/**
 * Utility functions for formatting various data types in a consistent manner.
 * This module exports functions that format data for display in UI components.
 * Each formatter function is designed to handle specific data types and use cases.
 */

// Importing external dependencies
import { Dropdown } from "react-bootstrap";
import { Link } from "react-router-dom";

import { Rate } from "antd";
import { PhoneNumber } from "../../utils";
import { AntTooltip, Popovers, Switch } from "../Antd";
// import { EditCommissionForm } from "../Form";
import { ActionButton } from "../UiElement";

/**
 * Formats an image cell for display in a table.
 * @param {string} cell - Image URL or source.
 * @param {string} text - Alternative text if the image is not available.
 * @returns {ReactNode} - Formatted image element.
 */
export const imageFormatter = (cell, text) => {
  return (
    <>
      {cell ? (
        <img
          className="category-img rounded-3"
          style={{ width: '40px', height: '40px', objectFit: 'cover' }}
          src={cell}
          alt="img"
        />
      ) : (
        text
      )}
    </>
  );
};

/**
 * Formats a logo cell for display in a table.
 * @param {string} path - Logo image path or source.
 * @param {string} text - Alternative text if the logo is not available.
 * @returns {ReactNode} - Formatted logo element.
 */
export const logoFormatter = (path, text) => {
  return (
    <>
      {path ? (
        <div className="table-img">
          <img src={path} alt="Category1" />
        </div>
      ) : (
        text
      )}
    </>
  );
};

/**
 * Returns the appropriate button element based on the specified action.
 * @param {Object} data - Button configuration object.
 * @returns {ReactNode} - Button element.
 */
function getButton(data) {
  let element;
  if (data.action === "redirect") {
    element = (
      <Link to={data.path}>
        <em className={data.icon} /> {data.name}
      </Link>
    );
  } else if (data.action === "modal") {
    element = (
      <Link
        to="#"
        onClick={(e) => {
          e.preventDefault();
          data.onClickHandle();
        }}
      >
        <em className={data.icon} /> {data.name}
      </Link>
    );
  } else if (data.action === "confirm") {
    element = (
      <Link
        to="#"
        onClick={(e) => {
          e.preventDefault();
          data.onClickHandle();
        }}
      >
        <em className={data.icon} /> {data.name}
      </Link>
    );
  }

  return element;
}

/**
 * Formats a set of action buttons in a dropdown menu.
 * @param {Array} options - Array of button configuration objects.
 * @returns {ReactNode} - Formatted dropdown menu with action buttons.
 */
export function actionFormatter(options) {
  return (
    <>
      <div className="text-end">
        <Dropdown className="position-static" >
          <Dropdown.Toggle as="a" className="btn btn-icon btn-trigger">
            <em className="icon ni ni-more-h" />
          </Dropdown.Toggle>
          <Dropdown.Menu align="end" size="sm" className="wide-xs">
            <ul className="link-list-plain">
              {options.map((item, key) => {
                return (
                  <li
                    className="action_list"
                    key={key}
                    onClick={(e) => {
                      e.stopPropagation(); // Prevent event bubbling
                      document.body.click(); // Simulate an outside click to close the dropdown
                    }}
                  >
                    {" "}
                    {getButton(item)}{" "}
                  </li>
                );
              })}
            </ul>
          </Dropdown.Menu>
        </Dropdown>
      </div>
    </>
  );
}

/**
 * Formats an action button based on the provided status.
 * @param {string} btnText - Text for the action button.
 * @param {string} status - Current status.
 * @param {Function} onHandleAction - Callback function for button click.
 * @returns {ReactNode} - Formatted action button.
 */
export const actionButtonFormatter = (btnText, status, onHandleAction) => {
  return (
    <>
      {status === "pending" ? (
        <ActionButton
          onHandleAction={() => {
            onHandleAction();
          }}
          btnText={btnText}
        />
      ) : (
        ""
      )}
    </>
  );
};

/**
 * Formats a link element with optional extra class name.
 * @param {string} cell - Text to be displayed in the link.
 * @param {string} link - URL for the link. Default is "#".
 * @param {string} extraClassName - Additional class names for styling.
 * @returns {ReactNode} - Formatted link element.
 */
export const linkFormatter = (cell, link = "#", extraClassName = "") => {
  return (
    <Link className={`${extraClassName}`} to={link}>
      {cell}
    </Link>
  );
};

/**
 * Formats a user card with name, image, path, and status.
 * @param {string} name - User's name.
 * @param {string} imgPath - Path to the user's image.
 * @param {string} path - URL path associated with the user.
 * @param {string} status - User's status.
 * @returns {ReactNode} - Formatted user card element.
 */
export const nameImageFormatter = (name, imgPath, path, status) => {
  return (
    <div>
      {name ? (
        <Link to={path} className="user-card">
          <div className="user-avatar">
            <img src={imgPath} alt="UserImage" />
          </div>
          <div className="user-info">
            <span className="user-name fw-medium">{name}</span>
            <span className="d-block">{status}</span>
          </div>
        </Link>
      ) : (
        "-"
      )}
    </div>
  );
};

/**
 * Formats a user card with phone number, email, and path.
 * @param {string} number - User's phone number.
 * @param {string} email - User's email address.
 * @param {string} path - URL path associated with the user.
 * @returns {ReactNode} - Formatted user card element.
 */
export const phoneEmailFormatter = (number, email, path) => {
  return (
    <div>
      {number ? (
        <Link to={path} className="user-card">
          <div className="user-info">
            <span className="user-name fw-medium">{number}</span>
            <span className="d-block">{email}</span>
          </div>
        </Link>
      ) : (
        "-"
      )}
    </div>
  );
};

/**
 * Formats a status badge based on the provided status value.
 * @param {string} cell - Status value to be formatted.
 * @returns {ReactNode} - Formatted status badge element.
 */
export const statusFormatter = (cell) => {
  // Define arrays for different status categories
  const successArr = [
    "active",
    "Active",
    "Inactive",
    "actived",
    "inStock",
    "delivered",
    "paid",
    "accepted",
    "assigned",
    "completed",
    "approved",
    "succeeded",
  ];
  const failedArr = [
    "rejected",
    "outOfStock",
    "cancelled",
    "inactive",
    "deActived",
    "reject",
    "canceled",
    "deleted",
    "failed",
  ];
  const onGoingArr = [
    "scheduled",
    "pending",
    "lowStock",
    "pendingApproval",
    "readyToShip",
    "lowInventory",
    "packed",
    "pickedUp",
  ];
  const pastArr = ["expired"];
  const doneArr = ["shipped"];
  const incompleteArr = ["profileInComplete", "credited", "received", "refund"];

  const statusArr = {
    active: "Active",
    Active: "Active",
    Inactive: "Inactive",
    actived: "Actived",
    succeeded: "Success Transactions",
    inactive: "Inactive",
    deActived: "Deactived",
    deleted: "Deleted",
    scheduled: "Scheduled",
    expired: "Expired",
    delivered: "Delivered",
    shipped: "Shipped",
    pending: "Pending",
    rejected: "Rejected",
    failed: "Failed Transactions",
    outOfStock: "Out of Stock",
    lowStock: "Low Stock",
    inStock: "In stock",
    pendingApproval: "Pending Approval",
    paid: "Paid",
    readyToShip: "Ready to Ship",
    cancelled: "Cancelled",
    received: "Received",
    profileInComplete: "Profile Incomplete",
    reject: "Rejected",
    lowInventory: "Low Inventory",
    credited: "Credited",
    accepted: "Accepted",
    assigned: "Assigned",
    packed: "Packed",
    pickedUp: "pickedUp",
    completed: "Completed",
    canceled: "canceled",
    refund: "Refund",
    approved: "Approved",
  };
  let data;
  // Determine the category of the status and apply the corresponding badge
  if (successArr.includes(cell)) {
    data = (
      <span className="badge rounded-pill badge-dim bg-success badge-sm">
        {statusArr?.[cell]}
      </span>
    );
  } else if (failedArr.includes(cell)) {
    data = (
      <span className="badge rounded-pill badge-dim bg-danger badge-sm">
        {statusArr?.[cell]}
      </span>
    );
  } else if (onGoingArr.includes(cell)) {
    data = (
      <span className="badge rounded-pill badge-dim bg-warning badge-sm">
        {statusArr?.[cell]}
      </span>
    );
  } else if (pastArr.includes(cell)) {
    data = (
      <span className="badge rounded-pill badge-dim bg-secondary badge-sm">
        {statusArr?.[cell]}
      </span>
    );
  } else if (doneArr.includes(cell)) {
    data = (
      <span className="badge badge-dot text-purple">{statusArr?.[cell]}</span>
    );
  } else if (incompleteArr.includes(cell)) {
    data = (
      <span className="badge rounded-pill badge-dim bg-info badge-sm">
        {statusArr?.[cell]}
      </span>
    );
  }

  return data;
};

/**
 * Formats a phone number using the PhoneNumber component.
 * @param {string} countryCode - Country code for the phone number.
 * @param {string} phoneNumber - Phone number to be formatted.
 * @returns {ReactNode} - Formatted phone number element.
 */
export const phoneNumberFormatter = (countryCode, phoneNumber) => {
  return <PhoneNumber countryCode={countryCode} contactNumber={phoneNumber} />;
};

/**
 * Formats a mobile number with country code and number.
 * @param {string} countryCode - Country code for the mobile number.
 * @param {string} number - Mobile number to be formatted.
 * @returns {ReactNode} - Formatted mobile number element.
 */
export const mobileFormatter = (countryCode, number) => {
  return <>{number ? ` ${countryCode} ${" "} ${number}` : "-"}</>;
};

/**
 * Formats a percentage value for display.
 * @param {number} val - Percentage value to be formatted.
 * @returns {ReactNode} - Formatted percentage value element.
 */
export const percentageFormatter = (val) => {
  return <>{val ? `${val} %` : "-"}</>;
};

/**
 * Formats a serial number based on pagination information.
 * @param {number} page - Current page number.
 * @param {number} sizePerPage - Number of items per page.
 * @param {number} index - Index of the item.
 * @returns {number} - Formatted serial number.
 */
export const serialNumberFormatter = (page, sizePerPage, index) => {
  return (page - 1) * sizePerPage + index + 1;
};

/**
 * Checks and returns valid data or a default dash.
 * @param {any} data - Data to be checked for validity.
 * @returns {ReactNode} - Valid data or default dash.
 */
export const checkValidData = (data) => {
  return data || "-";
};

/**
 * Checks and returns valid count or zero.
 * @param {number} data - Count to be checked for validity.
 * @returns {ReactNode} - Valid count or zero.
 */
export const checkValidCount = (data) => {
  return <>{data || 0}</>;
};

/**
 * Checks and returns valid price or default zero.
 * @param {number} data - Price to be checked for validity.
 * @returns {ReactNode} - Valid price or default zero.
 */
export const checkValidPrice = (data) => {
  return <>{data ? `$ ${data}` : "0"}</>;
};

/**
 * Formats a currency amount based on the specified type (INR or USD).
 * @param {number} dollar - Amount to be formatted.
 * @param {string} type - Currency type (INR or USD).
 * @returns {ReactNode} - Formatted currency amount.
 */
export const currencyFormatter = (dollar, type) => {
  const numericValue = typeof dollar === "string" ? parseFloat(dollar) : dollar;
  return (
    <>
      {numericValue
        ? numericValue?.toLocaleString(type === "INR" ? `en-IN` : `en-US`, {
          style: "currency",
          currency: `${type}`,
        })
        : "0"}
    </>
  );
};

/**
 * Formats a full name with first name and last name.
 * @param {string} firstName - First name.
 * @param {string} lastName - Last name.
 * @returns {ReactNode} - Formatted full name.
 */
export const nameFormatter = (firstName, lastName) => {
  return <>{firstName ? ` ${firstName} ${" "} ${lastName}` : "-"}</>;
};

/**
 * Capitalizes the first letter of a string.
 * @param {string} string - String to be capitalized.
 * @returns {string} - Capitalized string.
 */
export const capitalizeFirstLetter = (string) => {
  return (
    typeof string === "string" &&
    string?.charAt(0).toUpperCase() + string.slice(1)
  );
};

/**
 * Capitalizes the first letter of a string.
 * @param {string} data - String to be formatted.
 * @returns {string} - Formatted string with the first letter capitalized.
 */
export const textFormatter = (data) => {
  return data && data?.charAt(0)?.toUpperCase() + data.slice(1);
};

/**
 * Checks and returns a valid date with a specified formatter or a default dash.
 * @param {any} data - Date data to be checked for validity.
 * @param {string} formatter - Date formatter.
 * @returns {ReactNode} - Valid date or default dash.
 */
export const checkValidDateFormatter = (data, formatter) => {
  return <>{data ? formatter : "-"}</>;
};

/**
 * Truncates a string to a specified size and adds an ellipsis if needed.
 * @param {string} source - Source string to be truncated.
 * @param {number} size - Size limit for the truncated string.
 * @returns {string} - Truncated string with an optional ellipsis.
 */
export function truncate(source, size) {
  return source.length > size ? `${source.slice(0, size - 1)}…` : source;
}

/**
 * Formats an address, displaying a tooltip for long addresses.
 * @param {string} cell - Address to be formatted.
 * @returns {ReactNode} - Formatted address element.
 */
export const addressFormatter = (cell) => {
  let addressLength = cell.length;
  return (
    <>
      {addressLength > 20 ? (
        <AntTooltip placement="top" promptText={`${cell}`}>
          {truncate(cell, 20)}
        </AntTooltip>
      ) : (
        truncate(cell, 20)
      )}
    </>
  );
};

/**
 * Checks and returns a valid count or a default dash.
 * @param {number} data - Count to be checked for validity.
 * @returns {ReactNode} - Valid count or default dash.
 */
export const checkCount = (data) => {
  return <>{data > 0 ? data : "-"}</>;
};

/**
 * Formats a decimal value to display two decimal places.
 * @param {number} cell - Decimal value to be formatted.
 * @returns {string} - Formatted decimal value.
 */
export const decimalValueFormatter = (cell) => {
  if (Number(cell) >= 0) {
    return Number(cell).toFixed(2);
  } else {
    return ``;
  }
};

/**
 * Extracts text content from HTML.
 * @param {string} html - HTML content.
 * @returns {string} - Extracted text content.
 */
export function getText(html) {
  let divContainer = document.createElement("div");
  divContainer.innerHTML = html;
  return divContainer.textContent || divContainer.innerText || "";
}

/**
 * Formats a link for display, showing a shortened version if it exceeds 30 characters.
 * @param {string} data - Link to be formatted.
 * @returns {ReactNode} - Formatted link element.
 */
export const showLinkFormatter = (data) => {
  if ([undefined, null, false].includes(data)) {
    return <>-</>;
  }
  if (data.length < 30) {
    return <a href={data}>{data}</a>;
  }
  return <a href={data}>{data.substring(0, 30)}...</a>;
};

/**
 * Formats a switch element for display.
 * @param {boolean} checked - Current state of the switch.
 * @param {Function} onChange - Function to handle switch state changes.
 * @param {string} extraClassName - Additional class names for styling.
 * @returns {ReactNode} - Formatted switch element.
 */
export const switchFormatter = (checked, onChange, id, extraClassName = "") => {
  return (
    <Switch
      checked={checked}
      className={`${extraClassName}`}
      onChange={(val) => onChange(val, id)}
    />
  );
};

/**
 * Formats a rating element for display.
 * @param {string} extraClassName - Additional class names for styling.
 * @returns {ReactNode} - Formatted rating element.
 */
export const ratingFormatter = (extraClassName = "") => {
  return <Rate className={` ${extraClassName}`} disabled defaultValue={2} />;
};

/**
 * Formats a link as a modal trigger.
 * @param {string} cell - Text or content of the link.
 * @param {Function} onClickHandle - Function to handle click events.
 * @param {string} extraClassName - Additional class names for styling.
 * @returns {ReactNode} - Formatted modal trigger link element.
 */
export const modalFormatter = (cell, onClickHandle, extraClassName = "") => {
  return (
    <Link
      className={` ${extraClassName}`}
      to="#"
      onClick={(e) => {
        e.preventDefault();
        onClickHandle();
      }}
    >
      {cell}
    </Link>
  );
};

/**
 * Creates a logo based on the user's full name.
 * @param {string} fullName - Full name of the user.
 * @returns {string} - Logo created from the user's name initials.
 */
export const logoCreater = (fullName = "") => {
  const words = fullName?.split(" ")?.filter((word) => word.trim() !== "");
  const firstName = words?.[0] || "";
  if (words.length >= 2) {
    const lastName = words?.[1];
    if (/^[a-zA-Z]*$/.test(lastName))
      return `${firstName?.[0]}${lastName?.[0]}`;
    else return firstName?.substring(0, 2);
  } else return firstName?.substring(0, 2);
};

/**
 * Formats a user card with an image, name, and status.
 * @param {string} name - User's name.
 * @param {string} imgPath - Path to the user's image.
 * @param {string} status - User's status.
 * @returns {ReactNode} - Formatted user card element.
 */
export const userNameImageFormatter = (name, imgPath, status) => {
  return (
    <>
      {name ? (
        <Link className="user-card d-flex align-items-center">
          <div className="user-img position-relative">
            <div className="userAvatar">
              <img src={imgPath} alt="UserImage" crossOrigin="anonymous" />
            </div>
            {status && <span className={`statusdot statusdot-${status}`} />}
          </div>
          <div className="user-info">
            <h3 className="user-name font-sb">{name}</h3>
          </div>
        </Link>
      ) : (
        "-"
      )}
    </>
  );
};

/**
 * Formats a user dropdown with a name and associated data.
 * @param {string} name - User's name.
 * @param {Array} data - Array of dropdown items with paths and names.
 * @returns {ReactNode} - Formatted user dropdown element.
 */
export const userDropdownFormatter = (name, data) => {
  const item = data?.map((items) => {
    return (
      <li>
        <Link
          className="dropdown-item"
          onClick={document.body.click()}
          to={items.path}
        >
          {items.name}
        </Link>
      </li>
    );
  });
  return (
    <Popovers
      placement="bottom"
      overlayClassName="channelInfo"
      title=""
      content={<ul className="list-unstyled m-0">{item}</ul>}
      trigger="hover"
    >
      <span className="cursor-pointer m-0">{name}</span>
    </Popovers>
  );
};

/**
 * Formats a amount with currency data.
 * @param {number} name - Amount.
 * @returns {ReactNode} - Formatted currency element.
 */
export const dollarFormatter = (amount) => {
  return <>{amount ? `$${amount}` : "-"}</>;
};
